// Копирайт совместимости — spec/08_copy.md, раздел «Совместимость» (без пула вердиктов —
// он в copy/pools.ts) + подписи формы шага 2 из spec/05_compatibility.md (в 08_copy.md
// дословно не заведены, но по правилу 16 CLAUDE.md весь текст интерфейса живёт в этом модуле).
//
// ГРАНИЦА ГОЛОСА (docs/domain.md §8). Луня ведёт шаги, чипы, кнопки и строку-приговор в шапке
// результата. САМ РАЗБОР под шкалами голосом не пишется — он приходит с бэкенда
// (`apps/api/src/content/pools.ts`) и объясняет число методом, а не образами.
//
// Отклонения от 08_copy.md — по правке основателя «текст должен соответствовать тому, что
// реально посчитано»:
//   • «Расчёт один — упаковка разная» перестало быть правдой: у каждой линзы свои веса точек
//     и свой набор пар на сферу (`contracts/domain/compatScore.ts`, `compatAspects.ts`);
//   • «пять планет» и «остальные четыре планеты» — неточный термин: среди пяти считаемых
//     точек Солнце и Луна — светила. Набор называется поимённо (docs/domain.md §8, словарь);
//   • время и город теперь В РАСЧЁТЕ (`apps/api/src/astro/synastry.ts` строит карту на момент
//     рождения по исторической зоне города, при полных данных обеих сторон добавляется
//     асцендент). Прежние тексты «на расчёт это не влияет» сняты — они стали неправдой;
//   • но и обратного обещания нет: строка под шкалами называет, чего расчёту не хватило
//     и у КОГО, — по `own_precision` / `target_precision` из ответа бэкенда;
//   • в режиме «только знак» остальные четыре планеты приравниваются к знаку Солнца
//     (`compatChartFromSign`) — это грубее, чем звучало прежнее «посчитаю примерно».
import { textByGender } from '@lunya/contracts'
import type { CompatPrecision } from '@lunya/contracts'

import type { CompatCopy } from './types'

/**
 * Чего не хватает карте на этой ступени точности — в винительном падеже, для оборота
 * «не хватает {…}». `null` — ступень `exact`, не хватает ничего.
 */
const MISSING_BY_PRECISION: Record<CompatPrecision, string | null> = {
  exact: null,
  time_no_city: 'города рождения',
  date_only: 'времени и города рождения',
  sign_only: 'даты рождения',
}

export const compat: CompatCopy = {
  step1_title: 'Кого проверяем? 👀',
  step1_note:
    'Для половинки смотрю чувства, страсть, быт и разговоры. У каждой линзы свои веса у Солнца, Луны, Меркурия, Венеры и Марса — считаю по-разному.',
  step2_title: 'Кто именно?',
  // Хвост «подставлю всё, что знаю» снят — как и в `natal.subject_strip` (правка редактора):
  // ряд кружков и так показывает, что данные подставятся.
  step2_nodes: 'Из твоих уз',
  step2_deeper: 'Время и город, если знаешь',
  step2_deeper_s: 'с ними считаю на точный момент рождения, а не на полдень',
  // Подставлять чужие время и город молча нельзя: их указывал о себе другой человек. Строка
  // называет источник и напоминает, что поля живые, — чтобы никто не решил, будто это он сам
  // когда-то их вписал. Формулировка без рода: склонять здесь нечего и незачем.
  step2_from_link: ({ name }) => `Время и город — из вашей узы: так они записаны у ${name}`,
  // Было «считаю и забываю» — неправда: `CompatCheck` хранит имя, дату, время и город
  // проверяемого, иначе не было бы истории проверок и повторяемого результата. Обещать
  // тут можно только то, что правда: никто, кроме самого человека, этих данных не видит.
  step2_privacy:
    'Данные чужих людей я не показываю никому — они лежат только в твоей истории проверок.',
  step2_cta: 'Ну-ка, посчитаем ✨',
  sign_note: ({ name }) =>
    `По одному знаку это грубая прикидка: Луну, Меркурий, Венеру и Марс я приравниваю к знаку Солнца. Скажешь дату ${name} — посчитаю по-настоящему.`,
  share: 'Поделиться',
  again: 'Вернуться',
  // Строка под шкалами. Просит ровно то, чего правда не хватает, и у той стороны, у которой
  // не хватает, — по тому же правилу, что и `onb.a3.upsell`. Про второго говорим местоимением:
  // склонять имена мы не умеем, а «время рождения Дима» — не по-русски.
  precision_note: ({ own, target, gender }) => {
    if (target === 'sign_only') {
      const theirs = textByGender(
        gender,
        '{его|её} дату рождения',
        'дату рождения второго человека',
      )
      return `Считала по одному знаку — это грубая прикидка. Скажешь ${theirs}, посчитаю по-настоящему.`
    }

    const mine = MISSING_BY_PRECISION[own]
    const theirs = MISSING_BY_PRECISION[target]
    const whose = textByGender(gender, '{у него|у неё}', 'у второго')

    if (!mine && !theirs) {
      return 'Время и город есть у обоих — карты построены на точный момент рождения, и асцендент тоже в расчёте.'
    }
    // Когда обеим сторонам не хватает одного и того же, перечислять это дважды незачем:
    // «не хватает времени и города рождения у тебя и времени и города рождения у него».
    if (mine && theirs) {
      return mine === theirs
        ? `Точнее будет с полными данными: не хватает ${mine} — и у тебя, и ${whose}.`
        : `Точнее будет с полными данными: не хватает ${mine} у тебя и ${theirs} ${whose}.`
    }
    if (theirs) return `Точнее будет с полными данными: не хватает ${theirs} ${whose}.`
    return `Точнее будет с полными данными: не хватает ${mine} у тебя — это дописывается в профиле.`
  },
  // Проверки, посчитанные прежней формулой, остаются в истории с тем числом, которое человеку
  // показали. Молча пересчитать их значило бы подменить уже увиденный результат.
  history_outdated: 'Посчитано прежней формулой — новая проверка даст другое число.',
  share_message: 'Проверь свою пару у Луни 🐾',
  // Было «одна и та же пара дат»: теперь в расчёт входят ещё время и город, и повторить
  // результат можно только тем же полным набором данных.
  history_note:
    'Результат не меняется при повторной проверке — одни и те же данные всегда дают одно и то же число.',
  analysis_overline: 'РАЗБОР ЛУНИ',
  field_name: 'Имя',
  field_gender: 'Пол',
  field_accuracy: 'Точность',
  field_birth_date: 'Дата рождения',
  field_birth_time: 'Время рождения',
  field_birth_city: 'Город рождения',
  accuracy_date: 'Знаю дату',
  accuracy_sign: 'Только знак',
  gender_m: 'Он',
  gender_f: 'Она',
  sign_grid_title: 'ЕГО ЗНАК',
  sign_grid_title_f: 'ЕЁ ЗНАК',
  history_title: ({ gender }) => textByGender(gender, 'Кого ты проверял{|а}', 'Твои проверки'),
  history_empty_title: ({ gender }) =>
    textByGender(gender, 'Пока никого не {проверял|проверяла}', 'Проверок пока нет'),
  history_empty_description: 'Заведи первую проверку — результат сохранится здесь.',
}
